import type {IconButtonProps} from '@primer/react'
import {Button, IconButton} from '@primer/react'
import {forwardRef} from 'react'

import {type CommandId, getCommandMetadata, getKeybinding} from '../commands'
import {useCommandsContext} from '../commands-context'
import {START_PULL_REQUEST_BUTTON_TEXT} from '../constants'

export interface CommandIconButtonProps extends Omit<IconButtonProps, 'aria-label' | 'aria-labelledby'> {
  commandId: CommandId
  /** If `aria-label` is not provided, the button will render the command name as its label by default. */
  ['aria-label']?: IconButtonProps['aria-label']
  showStartPullRequest?: boolean
}

/**
 * `CommandButton` is a wrapper around `@primer/react` `Button`, but instead of an `onClick` handler it takes a
 * command ID and handles clicks by emitting command trigger events.
 *
 * If the command is gated by a disabled feature flag, nothing will render.
 */
export const CommandIconButton = forwardRef<HTMLButtonElement, CommandIconButtonProps>(
  (
    {commandId, ['aria-label']: ariaLabel, onClick: externalOnClick, icon, showStartPullRequest, ...forwardProps},
    ref,
  ) => {
    const metadata = getCommandMetadata(commandId)
    const {triggerCommand} = useCommandsContext()

    if (!metadata) return null

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      externalOnClick?.(event)
      if (!event.defaultPrevented) triggerCommand(commandId, event.nativeEvent)
    }

    if (showStartPullRequest) {
      return (
        <Button
          onClick={handleClick}
          ref={ref}
          leadingVisual={icon}
          keybindingHint={getKeybinding(commandId)}
          {...forwardProps}
        >
          {START_PULL_REQUEST_BUTTON_TEXT}
        </Button>
      )
    }

    return (
      <IconButton
        aria-label={ariaLabel ?? metadata.name}
        onClick={handleClick}
        icon={icon}
        ref={ref}
        keybindingHint={getKeybinding(commandId)}
        {...forwardProps}
      />
    )
  },
)
CommandIconButton.displayName = 'CommandIconButton'
